import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors.ts';

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${props => props.stretch ? '100%' : 'fit-content'};
`;

const TabList = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
  justify-content: ${props => props.align === 'center' ? 'center' : 'flex-start'};

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const TabButton = styled.button`
  font-size: 1rem;
  font-weight: ${props => props.active ? 600 : 400};
  padding: ${props => props.active ? '1.2rem 3rem' : '1.2rem 2rem'};
  border: ${props => props.active ? '2px solid ' + colors.transparent : '2px solid ' + colors.transparent}; 
  border-radius: 4rem;
  background: ${props => props.active ? colors.black : colors.transparent};
  backdrop-filter: ${props => props.active ? 'blur(10px)' : 'none'};
  color: ${props => props.active ? colors.white : colors.neutral[400]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? colors.black : colors.neutral[100] + '90'};
    border: ${props => props.active ? '2px solid ' + colors.transparent : '2px solid ' + colors.neutral[100]}; 
    color: ${props => props.active ? colors.white : colors.black};
  }

  @media (max-width: 768px) {
    padding: ${props => props.active ? '0.8rem 2rem' : '0.8rem 1.5rem'};
  }
`;

const TabPanel = styled.div`
  opacity: ${props => props.active ? 1 : 0};
  transform: translateY(${props => props.active ? '0' : '20px'});
  transition: opacity 0s ease, transform 0.3s ease;
  visibility: ${props => props.active ? 'visible' : 'hidden'};
  padding: 4rem 0rem;
  height: ${props => props.active ? 'auto' : '0'};
  position: ${props => props.active ? 'unset' : 'absolute'};
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: unset;
  overflow: ${props => props.active ? 'visible' : 'hidden'};
`;

export function Tabs({ children, defaultActiveTab = 0, stretch = false, align = 'left' }) {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  const tabs = React.Children.toArray(children).filter(
    child => React.isValidElement(child) && child.type === Tab && child.props.children
  );

  return (
    <TabsContainer role="tablist" stretch={stretch}>
      <TabList align={align}>
        {tabs.map((tab, index) => (
          <TabButton
            key={index}
            active={activeTab === index}
            onClick={() => setActiveTab(index)}
            role="tab"
            aria-selected={activeTab === index}
            aria-controls={`panel-${index}`}
            id={`tab-${index}`}
          >
            {tab.props.label}
          </TabButton>
        ))}
      </TabList>
      {tabs.map((tab, index) => (
        <TabPanel 
          key={index} 
          active={activeTab === index}
          role="tabpanel"
          aria-labelledby={`tab-${index}`}
          id={`panel-${index}`}
          stretch={stretch}
        >
          {tab.props.children}
        </TabPanel>
      ))}
    </TabsContainer>
  );
}

export function Tab({ label, children }) {
  return null; // This component is just for structure
}
